import {
  Order,
  OrderShipment
} from '@/app/shared/interface/order.interface';
import { Component, Input, TransferState } from '@angular/core';
import { Product, ProductModel } from "@/app/shared/interface/product.interface";
import { ProductService } from "@/app/shared/services/product.service";
import { OrderShippingService } from "@/app/shared/services/order-shipping-service";
import {HttpErrorResponse, HttpResponse} from "@angular/common/http";
import {ToastrService} from "ngx-toastr";

@Component({
  selector: 'app-shipment-list',
  templateUrl: './shipment-list.component.html',
  styleUrl: './shipment-list.component.scss'
})
export class ShipmentListComponent {

  @Input() shipments: OrderShipment[] = [];
  @Input() order: Order | undefined;

  public detail: OrderShipment | undefined;

  productMap: { [sku: string]: Product } = {};

  constructor(private productService: ProductService,
    private orderShippingService: OrderShippingService,
    private toastr: ToastrService
  ) { }

  ngOnInit() {
    let allSkus = this.shipments.flatMap(s => s.shipmentItems?.map(i => i.productSku) || []);
    let comboSkus = this.shipments.flatMap(s => s.shipmentItems?.filter(i => i.combo_ProductSku).map(i => i.combo_ProductSku!) || []);
    allSkus = allSkus.concat(comboSkus);
    const missingSkus = allSkus.filter(sku =>
      !this.order?.items.find(i => i.sku === sku || i.skU2 === sku)
    );
    if (missingSkus.length > 0) {
      const body = JSON.stringify({ productSkus: missingSkus, limit: missingSkus.length });
      this.productService.getProductsForMember(body).subscribe({
        next: (res: ProductModel) => {
          const products = res?.products || res;
          if (Array.isArray(products)) {
            products.forEach(p => {
              if (p.sku) this.productMap[p.sku] = p;
              if (p.skU2) this.productMap[p.skU2] = p;
            });
          }
          // Nếu không tìm được sản phẩm, vẫn tạo placeholder với sku
          missingSkus.forEach(sku => {
            if (!this.productMap[sku]) this.productMap[sku] = { sku } as any;
          });
        },
        error: (err) => {
          console.error('Không lấy được sản phẩm:', err);
          missingSkus.forEach(sku => this.productMap[sku] = { sku } as any);
        }
      });
    }
  }

  hasDiscount(items: any[] | undefined): boolean {
    return items?.some(i => i.priceUnitAfterDiscount != null && i.priceUnitAfterDiscount != 0 && i.priceUnitAfterDiscount != i.price) ?? false;
  }

  downloadShipmentPDF() {
    if (!this.order?.orderId) return;

    this.orderShippingService
      .downloadShipmentPdf(this.order.orderId)
      .subscribe((res: Blob) => {

        const blob = new Blob([res], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `shipment-${this.order?.orderId}.pdf`;
        a.click();

        window.URL.revokeObjectURL(url);
      });
  }

  exportExcel(shipmentId?: number) {
    if (!this.order?.orderId) return;

    this.orderShippingService.exportShipmentExcel(this.order.orderId, shipmentId)
      .subscribe({
        next: (res: HttpResponse<Blob>) => {
          const blob = res.body!;
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = this.getFileNameFromHeader(res)
            ?? `shipment-${this.order?.orderId}${shipmentId ? '-' + shipmentId : ''}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (err: HttpErrorResponse) => this.handleExportError(err)
      });
  }

  private async handleExportError(err: HttpErrorResponse) {
    let message = 'Có lỗi xảy ra khi xuất file, vui lòng thử lại.';

    if (err.error instanceof Blob) {
      try {
        const text = await err.error.text();
        const json = JSON.parse(text);
        message = json.message ?? message;
      } catch {
        // body lỗi không phải JSON hợp lệ -> giữ message mặc định
      }
    }

    this.toastr.error(message);
  }

  private getFileNameFromHeader(res: HttpResponse<Blob>): string | null {
    const disposition = res.headers.get('Content-Disposition');
    if (!disposition) return null;

    // Ưu tiên filename*=UTF-8''... (chuẩn RFC 5987, hỗ trợ tiếng Việt/ký tự đặc biệt)
    const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/);
    if (utf8Match) return decodeURIComponent(utf8Match[1]);

    // Fallback: filename="..."
    const asciiMatch = disposition.match(/filename="?([^";]+)"?/);
    return asciiMatch ? asciiMatch[1] : null;
  }

  getProductInfo(sku: string): Product | undefined {
    // Nếu trong order item, map ra Product tạm thời
    const item = this.order?.items.find(i => i.sku === sku || i.skU2 === sku);
    if (item) {
      return {
        sku: item.sku,
        name: item.name || '',
        imageUrls: item.imgUrl ? [item.imgUrl] : [],
        slug: item.slug,
        productID: item.productID
      } as Product;
    }

    return this.productMap[sku];
  }
}
